<template>
    <span class="ft-host">
        <!-- Botão discreto para rever o tour quando quiser -->
        <button type="button" class="ft-replay" @click="start()" :aria-label="`Ver as novidades desta tela: ${tour.intro.title}`">
            <svg class="ft-replay-star" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 2.5l1.9 6.1 6.1 1.9-6.1 1.9L12 18.5l-1.9-6.1L4 10.5l6.1-1.9z" />
                <path class="ft-replay-mini" d="M19 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
            </svg>
            Novidades
        </button>

        <!-- Camada do tour: vai para o <body> para não ser cortada por overflow -->
        <div ref="layer" class="ft" :class="[`is-${phase}`, {'is-gliding': gliding}]" v-show="phase !== 'closed'"
            @keydown="onKeydown">
            <!-- Bloqueiam o clique fora do recorte; dentro dele a tela continua interativa -->
            <div class="ft-block" v-for="(b, i) in blockers" :key="`b${i}`" :style="b"></div>

            <div class="ft-frost" aria-hidden="true"></div>

            <div class="ft-hole" :style="holeStyle" aria-hidden="true">
                <span class="ft-ring"></span>
                <span class="ft-ring is-echo"></span>
            </div>

            <!-- Abertura -->
            <div v-if="phase === 'intro'" ref="dialog" class="ft-intro" role="dialog" aria-modal="true"
                :aria-labelledby="`${uid}-title`" :aria-describedby="`${uid}-text`">
                <div class="ft-intro-band" aria-hidden="true">
                    <svg class="ft-constellation" viewBox="0 0 440 120" preserveAspectRatio="xMidYMid slice">
                        <path class="ft-star s1" d="M64 34l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
                        <path class="ft-star s2" d="M372 26l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" />
                        <path class="ft-star s3" d="M318 82l1.6 4.4 4.4 1.6-4.4 1.6-1.6 4.4-1.6-4.4-4.4-1.6 4.4-1.6z" />
                        <path class="ft-star s4" d="M128 88l1.4 3.6 3.6 1.4-3.6 1.4-1.4 3.6-1.4-3.6-3.6-1.4 3.6-1.4z" />
                        <circle class="ft-dot d1" cx="210" cy="22" r="2" />
                        <circle class="ft-dot d2" cx="404" cy="70" r="1.6" />
                        <circle class="ft-dot d3" cx="30" cy="82" r="1.4" />
                        <circle class="ft-dot d4" cx="262" cy="100" r="1.8" />
                    </svg>
                    <span class="ft-badge">
                        <svg viewBox="0 0 24 24" focusable="false"><path d="M12 2.5l1.9 6.1 6.1 1.9-6.1 1.9L12 18.5l-1.9-6.1L4 10.5l6.1-1.9z" /></svg>
                    </span>
                </div>

                <div class="ft-intro-body">
                    <p class="ft-eyebrow">{{tour.intro.eyebrow}}</p>
                    <h2 class="ft-intro-title" :id="`${uid}-title`">{{tour.intro.title}}</h2>
                    <p class="ft-intro-text" :id="`${uid}-text`">{{tour.intro.text}}</p>

                    <ul class="ft-highlights">
                        <li v-for="(h, i) in tour.intro.highlights" :key="i" :style="{animationDelay: `${180 + i * 90}ms`}">
                            <span class="ft-highlight-icon"><fa :icon="h.icon" fixed-width /></span>
                            <span>{{h.text}}</span>
                        </li>
                    </ul>

                    <div class="ft-intro-actions">
                        <button type="button" class="ft-btn is-ghost" @click="finish()">Agora não</button>
                        <button type="button" ref="primary" class="ft-btn is-primary" @click="begin()">
                            {{availableSteps.length ? 'Mostrar as novidades' : 'Entendi'}}
                            <fa v-if="availableSteps.length" icon="arrow-right" class="ft-btn-arrow" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Passo -->
            <div v-if="phase === 'step' && current" ref="dialog" class="ft-card" :class="`is-${placement}`" :style="cardStyle"
                role="dialog" aria-modal="true" :aria-labelledby="`${uid}-step-title`">
                <div class="ft-card-inner" :key="index">
                    <p class="ft-eyebrow">Novidade · {{index + 1}} de {{availableSteps.length}}</p>
                    <h3 class="ft-card-title" :id="`${uid}-step-title`">{{current.title}}</h3>
                    <p class="ft-card-text" aria-live="polite">{{current.text}}</p>
                </div>

                <div class="ft-card-foot">
                    <span class="ft-progress" aria-hidden="true">
                        <span v-for="(s, i) in availableSteps" :key="i" class="ft-progress-dot"
                            :class="{'is-current': i === index, 'is-done': i < index}"></span>
                    </span>

                    <button type="button" class="ft-btn is-ghost is-small" v-if="index > 0" @click="go(index - 1)">Voltar</button>
                    <button type="button" ref="primary" class="ft-btn is-primary is-small" @click="next()">
                        {{isLast ? 'Entendi' : 'Próximo'}}
                    </button>
                </div>

                <button type="button" class="ft-skip" v-if="!isLast" @click="finish()">Pular tour</button>
            </div>
        </div>
    </span>
</template>

<script>
// Tour de novidades: cartão de abertura + holofote que desliza de área em área.
// Usado só nas telas redesenhadas (grid e detalhe de pedido, detalhe de produto).
// Os passos ficam em src/utils/tours/FeatureTours.ts e apontam para `data-tour="..."`.
import featureTourManager from '@/utils/managers/FeatureTourManager'

let uid = 0

const OPEN_DELAY = 650
const GLIDE_MS = 520
const PAD = 8
const GAP = 16
const MARGIN = 16
const CARD_WIDTH = 340

function findTarget(name) {
    const nodes = document.querySelectorAll(`[data-tour="${name}"]`)

    for (let i = 0; i < nodes.length; i++) {
        const r = nodes[i].getBoundingClientRect()

        if (r.width > 0 && r.height > 0) {
            return nodes[i]
        }
    }

    return null
}

export default {
    name: 'FeatureTourCmp',
    props: {
        tour: {
            type: Object,
            required: true,
        },
        // A tela terminou de carregar e os alvos já estão no DOM
        ready: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        uid += 1

        return {
            uid: `ft-${uid}`,
            phase: 'closed', // closed | intro | step
            index: 0,
            availableSteps: [],
            hole: null,
            card: {top: 0, left: 0, width: CARD_WIDTH},
            placement: 'bottom',
            gliding: false,
            openTimer: null,
            glideTimer: null,
            frame: null,
            returnFocus: null,
        }
    },
    computed: {
        current() {
            return this.availableSteps[this.index]
        },
        isLast() {
            return this.index >= this.availableSteps.length - 1
        },
        // Sem alvo (abertura) o recorte é um ponto no centro: a sombra cobre a tela toda
        holeRect() {
            if (this.hole) {
                return this.hole
            }

            const w = typeof window !== 'undefined' ? window.innerWidth : 0
            const h = typeof window !== 'undefined' ? window.innerHeight : 0

            return {top: h / 2, left: w / 2, width: 0, height: 0}
        },
        holeStyle() {
            const r = this.holeRect

            return {top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px`}
        },
        blockers() {
            const r = this.holeRect
            const bottom = r.top + r.height
            const right = r.left + r.width

            return [
                {top: 0, left: 0, right: 0, height: `${Math.max(r.top, 0)}px`},
                {top: `${bottom}px`, left: 0, right: 0, bottom: 0},
                {top: `${r.top}px`, left: 0, width: `${Math.max(r.left, 0)}px`, height: `${r.height}px`},
                {top: `${r.top}px`, left: `${right}px`, right: 0, height: `${r.height}px`},
            ]
        },
        cardStyle() {
            return {top: `${this.card.top}px`, left: `${this.card.left}px`, width: `${this.card.width}px`}
        },
    },
    watch: {
        ready: {
            immediate: true,
            handler(isReady) {
                if (isReady) {
                    this.scheduleAutoOpen()
                }
            },
        },
    },
    mounted() {
        document.body.appendChild(this.$refs.layer)
    },
    beforeDestroy() {
        this.unbind()
        clearTimeout(this.openTimer)
        clearTimeout(this.glideTimer)
        cancelAnimationFrame(this.frame)

        const layer = this.$refs.layer

        if (layer && layer.parentNode) {
            layer.parentNode.removeChild(layer)
        }
    },
    methods: {
        scheduleAutoOpen() {
            if (this.phase !== 'closed' || featureTourManager.hasSeen(this.tour.id, this.tour.version)) {
                return
            }

            clearTimeout(this.openTimer)
            this.openTimer = setTimeout(() => {
                // Algum modal do bootstrap aberto nesse meio tempo: não empilha por cima
                if (document.querySelector('.modal.show')) {
                    return
                }

                this.start()
            }, OPEN_DELAY)
        },
        start() {
            clearTimeout(this.openTimer)
            this.returnFocus = document.activeElement
            this.availableSteps = this.tour.steps.filter((s) => !!findTarget(s.target))
            this.index = 0
            this.hole = null
            this.phase = 'intro'
            this.bind()
            this.focusPrimary()
        },
        begin() {
            if (!this.availableSteps.length) {
                this.finish()
                return
            }

            // O cartão nasce onde estava a abertura e desliza até o primeiro alvo
            const cw = Math.min(CARD_WIDTH, window.innerWidth - MARGIN * 2)
            this.card = {top: Math.round(window.innerHeight / 2 - 100), left: Math.round((window.innerWidth - cw) / 2), width: cw}
            this.phase = 'step'
            this.go(0)
        },
        next() {
            if (this.isLast) {
                this.finish()
                return
            }

            this.go(this.index + 1)
        },
        go(index) {
            const step = this.availableSteps[index]
            const el = step && findTarget(step.target)

            if (!el) {
                // O alvo sumiu (filtro, troca de modo...): tenta o próximo ou encerra
                this.availableSteps.splice(index, 1)

                if (index < this.availableSteps.length) {
                    this.go(index)
                } else if (this.availableSteps.length) {
                    this.go(this.availableSteps.length - 1)
                } else {
                    this.finish()
                }

                return
            }

            this.index = index
            this.glide()

            const r = el.getBoundingClientRect()
            const tall = r.height > window.innerHeight * 0.55
            el.scrollIntoView({block: tall ? 'start' : 'center', behavior: this.reducedMotion() ? 'auto' : 'smooth'})

            this.$nextTick(() => {
                this.position()
                this.focusPrimary()
            })
        },
        finish() {
            featureTourManager.markSeen(this.tour.id, this.tour.version)
            this.phase = 'closed'
            this.hole = null
            this.unbind()

            const back = this.returnFocus

            if (back && typeof back.focus === 'function' && document.body.contains(back)) {
                back.focus()
            }
        },
        // Liga a transição do recorte só durante a troca de passo; no resto ele acompanha o scroll sem atraso
        glide() {
            clearTimeout(this.glideTimer)
            this.gliding = true
            this.glideTimer = setTimeout(() => {
                this.gliding = false
            }, GLIDE_MS)
        },
        reducedMotion() {
            return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        },
        focusPrimary() {
            this.$nextTick(() => {
                const btn = this.$refs.primary

                if (btn) {
                    btn.focus({preventScroll: true})
                }
            })
        },
        schedulePosition() {
            cancelAnimationFrame(this.frame)
            this.frame = requestAnimationFrame(this.position)
        },
        position() {
            if (this.phase !== 'step' || !this.current) {
                return
            }

            const el = findTarget(this.current.target)

            if (!el) {
                return
            }

            const vw = window.innerWidth
            const vh = window.innerHeight
            const t = el.getBoundingClientRect()
            const cardEl = this.$refs.dialog
            const cw = Math.min(CARD_WIDTH, vw - MARGIN * 2)
            const ch = cardEl ? cardEl.offsetHeight : 200
            const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max))

            // Recorte: o alvo com folga, limitado à janela; alvo alto é cortado para sobrar lugar ao cartão
            let top = Math.max(t.top - PAD, MARGIN)
            let bottom = Math.min(t.bottom + PAD, vh - MARGIN)
            const left = Math.max(t.left - PAD, MARGIN / 2)
            const right = Math.min(t.right + PAD, vw - MARGIN / 2)
            const roomForCard = vh - ch - GAP - MARGIN * 2
            const sideRoom = vw - right >= cw + GAP + MARGIN || left >= cw + GAP + MARGIN

            if (!sideRoom && bottom - top > roomForCard) {
                bottom = top + Math.max(roomForCard, 80)
            }

            if (bottom < top) {
                bottom = top
            }

            const hole = {top, left, width: Math.max(right - left, 0), height: bottom - top}
            const cx = hole.left + hole.width / 2

            let placement
            let cardTop
            let cardLeft

            if (bottom + GAP + ch <= vh - MARGIN) {
                placement = 'bottom'
                cardTop = bottom + GAP
                cardLeft = clamp(cx - cw / 2, MARGIN, vw - MARGIN - cw)
            } else if (top - GAP - ch >= MARGIN) {
                placement = 'top'
                cardTop = top - GAP - ch
                cardLeft = clamp(cx - cw / 2, MARGIN, vw - MARGIN - cw)
            } else if (vw - right >= cw + GAP + MARGIN) {
                placement = 'right'
                cardLeft = right + GAP
                cardTop = clamp(top, MARGIN, vh - MARGIN - ch)
            } else if (left >= cw + GAP + MARGIN) {
                placement = 'left'
                cardLeft = left - GAP - cw
                cardTop = clamp(top, MARGIN, vh - MARGIN - ch)
            } else {
                placement = 'inside'
                cardTop = vh - MARGIN - ch
                cardLeft = clamp(cx - cw / 2, MARGIN, vw - MARGIN - cw)
            }

            this.hole = hole
            this.placement = placement
            this.card = {top: Math.round(cardTop), left: Math.round(cardLeft), width: cw}
        },
        onKeydown(e) {
            if (this.phase === 'closed') {
                return
            }

            if (e.key === 'Escape') {
                e.preventDefault()
                this.finish()
            } else if (e.key === 'Tab') {
                this.trapFocus(e)
            } else if (this.phase === 'step' && e.key === 'ArrowRight') {
                e.preventDefault()
                this.next()
            } else if (this.phase === 'step' && e.key === 'ArrowLeft' && this.index > 0) {
                e.preventDefault()
                this.go(this.index - 1)
            }
        },
        // Teclado fora da camada (foco ficou na página): mesmo comportamento
        onDocumentKeydown(e) {
            const layer = this.$refs.layer

            if (layer && !layer.contains(e.target)) {
                this.onKeydown(e)
            }
        },
        trapFocus(e) {
            const dialog = this.$refs.dialog

            if (!dialog) {
                return
            }

            const items = Array.prototype.slice.call(dialog.querySelectorAll('button'))

            if (!items.length) {
                return
            }

            const first = items[0]
            const last = items[items.length - 1]

            if (!dialog.contains(document.activeElement)) {
                e.preventDefault()
                first.focus()
            } else if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        },
        bind() {
            this.unbind()
            document.addEventListener('keydown', this.onDocumentKeydown)
            window.addEventListener('scroll', this.schedulePosition, true)
            window.addEventListener('resize', this.schedulePosition)
        },
        unbind() {
            document.removeEventListener('keydown', this.onDocumentKeydown)
            window.removeEventListener('scroll', this.schedulePosition, true)
            window.removeEventListener('resize', this.schedulePosition)
        },
    },
}
</script>

<style lang="scss" scoped>
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;
$ease: cubic-bezier(.22, 1, .36, 1);

/* ── Botão "Novidades" ── */
.ft-host {
    display: inline-flex;
}

.ft-replay {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px 5px 9px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 999px;
    color: var(--brand);
    background: var(--tint-brand-soft);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color .2s, border-color .2s, transform .2s $ease;

    &:hover {
        background: var(--tint-brand-hover);
        border-color: var(--brand-soft);
        transform: translateY(-1px);

        .ft-replay-star {
            transform: rotate(72deg) scale(1.1);
        }

        .ft-replay-mini {
            opacity: 1;
        }
    }

    &:focus-visible {
        outline: 2px solid var(--focus-ring);
        outline-offset: 2px;
    }
}

.ft-replay-star {
    width: 15px;
    height: 15px;
    fill: currentColor;
    transition: transform .5s $ease;
}

.ft-replay-mini {
    opacity: .45;
    transition: opacity .3s;
}

/* ── Camada ── */
.ft {
    position: fixed;
    inset: 0;
    z-index: 1080;
    pointer-events: none;
    font-family: 'Source Sans Pro', system-ui, sans-serif;
}

.ft-block {
    position: fixed;
    pointer-events: auto;
}

.ft-frost {
    position: fixed;
    inset: 0;
    backdrop-filter: blur(3px) saturate(.9);
    opacity: 0;
    transition: opacity .45s ease;
}

.ft.is-intro .ft-frost {
    opacity: 1;
}

.ft-hole {
    position: fixed;
    border-radius: 14px;
    box-shadow: 0 0 0 100vmax var(--backdrop);
    animation: ft-veil-in .4s ease both;
}

.ft.is-gliding .ft-hole {
    transition: top .5s $ease, left .5s $ease, width .5s $ease, height .5s $ease;
}

.ft.is-gliding .ft-card {
    transition: top .5s $ease, left .5s $ease;
}

.ft.is-intro .ft-ring {
    display: none;
}

.ft-ring {
    position: absolute;
    inset: -3px;
    border: 2px solid var(--brand-soft);
    border-radius: 17px;
    box-shadow: 0 0 22px color-mix(in srgb, var(--brand-soft) 55%, transparent),
                inset 0 0 18px color-mix(in srgb, var(--brand-soft) 25%, transparent);
    animation: ft-breathe 2.4s ease-in-out infinite;

    &.is-echo {
        box-shadow: none;
        border-width: 1px;
        animation: ft-sonar 2.4s $ease infinite;
    }
}

/* ── Abertura ── */
.ft-intro {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 440px;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 32px);
    overflow: auto;
    border-radius: 22px;
    background: var(--surface-1);
    box-shadow: var(--shadow-lg);
    pointer-events: auto;
    transform: translate(-50%, -50%);
    animation: ft-pop .55s $ease both;
}

.ft-intro-band {
    position: relative;
    height: 116px;
    background: var(--brand-gradient);
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        inset: auto -10% -60% -10%;
        height: 120%;
        background: radial-gradient(closest-side, color-mix(in srgb, var(--text-on-brand) 22%, transparent), transparent);
        animation: ft-glow 6s ease-in-out infinite alternate;
    }
}

.ft-constellation {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

.ft-star,
.ft-dot {
    fill: var(--text-on-brand);
    transform-box: fill-box;
    transform-origin: center;
    animation: ft-twinkle 3.2s ease-in-out infinite;
}

.ft-star.s1 { animation-delay: .1s; }
.ft-star.s2 { animation-delay: .9s; }
.ft-star.s3 { animation-delay: 1.6s; opacity: .8; }
.ft-star.s4 { animation-delay: 2.2s; opacity: .7; }
.ft-dot { opacity: .6; animation-duration: 2.6s; }
.ft-dot.d2 { animation-delay: .7s; }
.ft-dot.d3 { animation-delay: 1.3s; }
.ft-dot.d4 { animation-delay: 1.9s; }

.ft-badge {
    position: absolute;
    left: 28px;
    bottom: -26px;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    border-radius: 18px;
    background: var(--surface-1);
    box-shadow: var(--shadow-md);
    animation: ft-badge-in .7s $ease .15s both;

    svg {
        width: 28px;
        height: 28px;
        fill: var(--brand);
        animation: ft-spin-in 1s $ease .3s both;
    }
}

.ft-intro-body {
    padding: 42px 28px 24px;
}

.ft-eyebrow {
    margin: 0 0 6px;
    color: var(--brand);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .16em;
    text-transform: uppercase;
}

.ft-intro-title {
    margin: 0 0 8px;
    color: var(--text-strong);
    font-family: $display;
    font-size: 26px;
    font-weight: 800;
    line-height: 1.12;
    letter-spacing: -.01em;
}

.ft-intro-text {
    margin: 0 0 18px;
    color: var(--text-soft);
    font-size: 15px;
    line-height: 1.5;
}

.ft-highlights {
    margin: 0 0 24px;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 0;
        color: var(--text);
        font-size: 14.5px;
        line-height: 1.35;
        animation: ft-rise .5s $ease both;

        & + li {
            border-top: 1px dashed var(--border-subtle);
        }
    }
}

.ft-highlight-icon {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    color: var(--brand);
    background: var(--tint-brand-bg);
    font-size: 14px;
}

.ft-intro-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

/* ── Botões ── */
.ft-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 18px;
    border: 0;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color .2s, color .2s, transform .2s $ease, box-shadow .2s;

    &:focus-visible {
        outline: 2px solid var(--focus-ring);
        outline-offset: 2px;
    }

    &.is-primary {
        color: var(--text-on-brand);
        background: var(--brand-fill);
        box-shadow: 0 6px 16px -8px color-mix(in srgb, var(--brand-fill) 80%, transparent);

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 20px -8px color-mix(in srgb, var(--brand-fill) 80%, transparent);

            .ft-btn-arrow {
                transform: translateX(3px);
            }
        }
    }

    &.is-ghost {
        color: var(--text-muted);
        background: transparent;

        &:hover {
            color: var(--text-strong);
            background: var(--surface-2);
        }
    }

    &.is-small {
        padding: 7px 14px;
        font-size: 13px;
    }
}

.ft-btn-arrow {
    font-size: 12px;
    transition: transform .2s $ease;
}

/* ── Cartão do passo ── */
.ft-card {
    position: fixed;
    padding: 18px 20px 14px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
    box-shadow: var(--shadow-lg);
    pointer-events: auto;
    animation: ft-pop-small .45s $ease both;

    &::before {
        content: '';
        position: absolute;
        top: 18px;
        bottom: 18px;
        left: 0;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: var(--brand-gradient);
    }
}

.ft-card-inner {
    animation: ft-rise .4s $ease both;
}

.ft-card-title {
    margin: 0 0 6px;
    color: var(--text-strong);
    font-family: $display;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.15;
}

.ft-card-text {
    margin: 0 0 14px;
    color: var(--text-soft);
    font-size: 14.5px;
    line-height: 1.5;
}

.ft-card-foot {
    display: flex;
    align-items: center;
    gap: 6px;
}

.ft-progress {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-right: auto;
}

.ft-progress-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--border-strong);
    transition: width .35s $ease, background-color .35s;

    &.is-done {
        background: var(--brand-soft);
    }

    &.is-current {
        width: 20px;
        background: var(--brand-fill);
    }
}

.ft-skip {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 2px 6px;
    border: 0;
    border-radius: 6px;
    color: var(--text-faint);
    background: transparent;
    font-size: 12px;
    cursor: pointer;

    &:hover {
        color: var(--text-muted);
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 2px solid var(--focus-ring);
    }
}

/* ── Animações ── */
@keyframes ft-veil-in {
    from { box-shadow: 0 0 0 100vmax transparent; }
}

@keyframes ft-pop {
    from { opacity: 0; transform: translate(-50%, -46%) scale(.94); }
}

@keyframes ft-pop-small {
    from { opacity: 0; transform: translateY(6px) scale(.97); }
}

@keyframes ft-rise {
    from { opacity: 0; transform: translateY(8px); }
}

@keyframes ft-badge-in {
    from { opacity: 0; transform: translateY(14px) scale(.8); }
}

@keyframes ft-spin-in {
    from { transform: rotate(-120deg) scale(.4); }
}

@keyframes ft-twinkle {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(.55); opacity: .45; }
}

@keyframes ft-glow {
    from { transform: translateX(-8%); }
    to { transform: translateX(8%); }
}

@keyframes ft-breathe {
    0%, 100% { opacity: 1; }
    50% { opacity: .55; }
}

@keyframes ft-sonar {
    from { opacity: .9; transform: scale(1); }
    to { opacity: 0; transform: scale(1.06, 1.12); }
}

@media (max-width: 575px) {
    .ft-intro-title {
        font-size: 22px;
    }

    .ft-intro-body {
        padding: 40px 20px 20px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .ft-hole,
    .ft-ring,
    .ft-intro,
    .ft-intro-band::after,
    .ft-badge,
    .ft-badge svg,
    .ft-star,
    .ft-dot,
    .ft-highlights li,
    .ft-card,
    .ft-card-inner {
        animation: none;
    }

    .ft.is-gliding .ft-hole,
    .ft.is-gliding .ft-card {
        transition: none;
    }

    .ft-ring.is-echo {
        display: none;
    }
}
</style>
